import type { FirewallEvents } from "./events.types";

/**
 * What FortiOS calls an event (its `logdesc`) in words a customer reads without knowing
 * IPsec's vocabulary (CTX-1264). One map, read by the Events table, the map's inspectors
 * and Observations, so the same event is never named two ways. A description not in
 * the map is shown as FortiOS wrote it — never hidden.
 */
const EVENT_NAMES: Record<string, string> = {
    "ipsec phase 1 error": "Phase 1 negotiation failed",
    "negotiate ipsec phase 1": "Phase 1 negotiation",
    "progress ipsec phase 1": "Phase 1 negotiation started",
    "progress ipsec phase 2": "Phase 2 negotiation started",
    "ipsec phase 2 error": "Phase 2 negotiation failed",
    "ipsec phase 2 status change": "Phase 2 status changed",
    "ipsec phase 2 status changed": "Phase 2 status changed",
    "ipsec connection status change": "Tunnel went up or down",
    "ipsec connection status changed": "Tunnel went up or down",
    "ipsec esp": "Encrypted packet dropped (ESP)",
    "ipsec dpd failed": "Peer stopped answering (DPD)",
    "ipsec tunnel statistics": "Tunnel statistics",
};

export function eventName(text: string): string {
    return EVENT_NAMES[text.trim().toLowerCase()] ?? text;
}

/** IPsec negotiation and transport events: the ones a tunnel, or its absence, explains. */
export const isIpsecEvent = (text: string) => /ipsec|phase [12]|\besp\b|\bike\b/i.test(text);

/**
 * "29 errors in the last 24 h". When the firewall's short log does not reach back a
 * day, the count is a floor — "36+ errors today" says so without a footnote.
 */
export function errorsSummary(events: Pick<FirewallEvents, "errors_24h" | "errors_24h_partial">): string {
    const { errors_24h: errors, errors_24h_partial: partial } = events;
    if (errors === 0) return partial ? "No errors recently" : "No errors in the last 24 h";
    const noun = errors === 1 && !partial ? "error" : "errors";
    return partial ? `${errors}+ ${noun} today` : `${errors} ${noun} in the last 24 h`;
}
